// Text layouts: create a versioned layout (canvas + layers referencing fields
// and bundled fonts), review it, and browse versions. Fonts and their license
// provenance are listed for reference.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../../lib/api.js';
import type { Concept, FontInfo, PersonalizationDef, TextLayout } from '../../lib/types.js';
import { StatusBadge } from '../../components/StatusBadge.js';

const SAMPLE_LAYERS = [
  {
    fieldKey: 'clubName',
    box: { x: 300, y: 150, width: 1000, height: 180 },
    fontId: 'pt-sans-bold',
    fontSize: 120,
    minFontSize: 30,
    maxFontSize: 140,
    color: '#222222',
    overflowBehavior: 'shrink_to_fit',
  },
  {
    fieldKey: 'memberName',
    box: { x: 300, y: 400, width: 1000, height: 150 },
    fontId: 'lobster-regular',
    fontSize: 90,
    minFontSize: 24,
    maxFontSize: 120,
    color: '#333333',
    overflowBehavior: 'shrink_to_fit',
  },
  {
    fieldKey: 'foundedYear',
    box: { x: 650, y: 600, width: 300, height: 90 },
    fontId: 'pt-sans-regular',
    fontSize: 60,
    minFontSize: 20,
    maxFontSize: 80,
    color: '#555555',
    overflowBehavior: 'shrink_to_fit',
    visibleWhenEmpty: false,
  },
];

export function TextLayoutsPage() {
  const queryClient = useQueryClient();
  const [conceptId, setConceptId] = useState('');
  const [defId, setDefId] = useState('');
  const [name, setName] = useState('Mug layout');
  const [canvas, setCanvas] = useState('{ "width": 1600, "height": 900 }');
  const [layersJson, setLayersJson] = useState(JSON.stringify(SAMPLE_LAYERS, null, 2));
  const [error, setError] = useState<string | null>(null);

  const conceptsQuery = useQuery({
    queryKey: ['concepts', 'approved'],
    queryFn: () => api.get<Concept[]>('/concepts?status=approved&pageSize=100'),
  });
  const defsQuery = useQuery({
    queryKey: ['personalization-defs', conceptId],
    queryFn: () => api.get<PersonalizationDef[]>(`/personalization-defs?conceptId=${conceptId}`),
    enabled: !!conceptId,
  });
  const fontsQuery = useQuery({
    queryKey: ['fonts'],
    queryFn: () => api.get<FontInfo[]>('/fonts'),
  });
  const listQuery = useQuery({
    queryKey: ['text-layouts'],
    queryFn: () => api.get<TextLayout[]>('/text-layouts?pageSize=100'),
  });

  const create = useMutation({
    mutationFn: () => {
      if (!conceptId || !defId) throw new Error('Select a concept and personalization definition');
      let canvasObj: unknown;
      let layers: unknown;
      try {
        canvasObj = JSON.parse(canvas);
        layers = JSON.parse(layersJson);
      } catch {
        throw new Error('Canvas and layers must be valid JSON');
      }
      return api.post('/text-layouts', {
        conceptId,
        personalizationDefId: defId,
        name,
        canvas: canvasObj,
        layers,
      });
    },
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['text-layouts'] });
    },
    onError: (e) => setError(e instanceof ApiClientError ? e.message : String(e)),
  });

  const review = useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: 'approved' | 'archived' }) =>
      api.post(`/text-layouts/${id}/review`, { decision }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['text-layouts'] }),
    onError: (e) => setError(e instanceof ApiClientError ? e.message : String(e)),
  });

  const concepts = conceptsQuery.data?.data ?? [];
  const defs = defsQuery.data?.data ?? [];
  const fonts = fontsQuery.data?.data ?? [];
  const layouts = listQuery.data?.data ?? [];

  return (
    <section>
      <h1>Text Layouts</h1>
      <p className="muted">Place personalization fields on artwork with bundled OFL fonts.</p>

      <div className="card">
        <h2>Available fonts</h2>
        <ul className="font-list">
          {fonts.map((f) => (
            <li key={f.fontId}>
              <code>{f.fontId}</code> — {f.family} {f.weight} · {f.license}
            </li>
          ))}
        </ul>
      </div>

      <div className="card">
        <h2>New layout</h2>
        <div className="form-row">
          <label htmlFor="tl-concept">Approved concept</label>
          <select id="tl-concept" value={conceptId} onChange={(e) => setConceptId(e.target.value)}>
            <option value="">Select a concept…</option>
            {concepts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title}
              </option>
            ))}
          </select>
        </div>
        <div className="form-row">
          <label htmlFor="tl-def">Personalization definition</label>
          <select
            id="tl-def"
            value={defId}
            onChange={(e) => setDefId(e.target.value)}
            disabled={!conceptId}
          >
            <option value="">Select a definition…</option>
            {defs.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name} (v{d.version}, {d.status})
              </option>
            ))}
          </select>
        </div>
        <div className="form-row">
          <label htmlFor="tl-name">Name</label>
          <input id="tl-name" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="form-row">
          <label htmlFor="tl-canvas">Canvas (JSON)</label>
          <input
            id="tl-canvas"
            className="mono"
            value={canvas}
            onChange={(e) => setCanvas(e.target.value)}
          />
        </div>
        <div className="form-row">
          <label htmlFor="tl-layers">Layers (JSON)</label>
          <textarea
            id="tl-layers"
            rows={16}
            className="mono"
            value={layersJson}
            onChange={(e) => setLayersJson(e.target.value)}
          />
        </div>
        {error && (
          <p role="alert" className="form-error">
            {error}
          </p>
        )}
        <button type="button" onClick={() => create.mutate()} disabled={create.isPending}>
          {create.isPending ? 'Creating…' : 'Create layout'}
        </button>
      </div>

      <div className="card">
        <h2>Layouts</h2>
        {layouts.length === 0 && <p className="muted">None yet.</p>}
        <table className="list-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Version</th>
              <th>Layers</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {layouts.map((l) => (
              <tr key={l.id}>
                <td>{l.name}</td>
                <td>v{l.version}</td>
                <td>{l.layers.length}</td>
                <td>
                  <StatusBadge status={l.status} />
                </td>
                <td>
                  {l.status === 'draft' && (
                    <button
                      type="button"
                      onClick={() => review.mutate({ id: l.id, decision: 'approved' })}
                    >
                      Approve
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
